import { useState, useRef, useEffect, useCallback } from 'react';

const MIN_THUMB_PERCENT = 15;
const MAX_THUMB_PERCENT = 60;
const HIDE_DELAY_MS = 1000;

const computeThumbPercent = () => {
  const doc = document.documentElement;
  const ratio = doc.clientHeight / doc.scrollHeight;
  return Math.max(MIN_THUMB_PERCENT, Math.min(MAX_THUMB_PERCENT, ratio * 100));
};

export const VerticalScrollIndicator = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ progress: 0, hasOverflow: false, thumbPercent: 30 });
  const [interacting, setInteracting] = useState(false);
  const [visible, setVisible] = useState(false);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ y: 0, startProgress: 0 });
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!draggingRef.current) {
        setVisible(false);
      }
    }, HIDE_DELAY_MS);
  }, []);

  const showBar = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    setVisible(true);
  }, []);

  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(8);
    }
  }, []);

  const updateScroll = useCallback(() => {
    if (draggingRef.current) return;
    const doc = document.documentElement;
    const maxScroll = doc.scrollHeight - doc.clientHeight;
    const hasOverflow = maxScroll > 0;
    const progress = hasOverflow ? window.scrollY / maxScroll : 0;
    setScrollInfo({ progress, hasOverflow, thumbPercent: computeThumbPercent() });
    showBar();
    scheduleHide();
  }, [showBar, scheduleHide]);

  const scrollToProgress = useCallback((p: number) => {
    const clamped = Math.max(0, Math.min(1, p));
    const doc = document.documentElement;
    const maxScroll = doc.scrollHeight - doc.clientHeight;
    window.scrollTo({ top: clamped * maxScroll });
    setScrollInfo({ progress: clamped, hasOverflow: true, thumbPercent: computeThumbPercent() });
  }, []);

  const handleTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const thumbHeight = rect.height * (scrollInfo.thumbPercent / 100);
    const usable = rect.height - thumbHeight;
    const progress = (clickY - thumbHeight / 2) / usable;
    scrollToProgress(progress);
  }, [scrollToProgress, scrollInfo.thumbPercent]);

  const handleThumbDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    draggingRef.current = true;
    setInteracting(true);
    clearTimeout(hideTimerRef.current);
    triggerHaptic();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartRef.current = { y: clientY, startProgress: scrollInfo.progress };

    const thumbPercent = scrollInfo.thumbPercent;

    const onMove = (ev: MouseEvent | TouchEvent) => {
      const track = trackRef.current;
      if (!track) return;
      const moveY = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
      const rect = track.getBoundingClientRect();
      const thumbHeight = rect.height * (thumbPercent / 100);
      const usable = rect.height - thumbHeight;
      const dy = moveY - dragStartRef.current.y;
      const dp = dy / usable;
      scrollToProgress(dragStartRef.current.startProgress + dp);
    };

    const onUp = () => {
      draggingRef.current = false;
      setInteracting(false);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      scheduleHide();
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onUp);
  }, [scrollInfo.progress, scrollInfo.thumbPercent, scrollToProgress, triggerHaptic, scheduleHide]);

  const handleTrackEnter = useCallback(() => {
    if (!draggingRef.current) {
      setInteracting(true);
      clearTimeout(hideTimerRef.current);
    }
  }, []);

  const handleTrackLeave = useCallback(() => {
    if (!draggingRef.current) {
      setInteracting(false);
      scheduleHide();
    }
  }, [scheduleHide]);

  useEffect(() => {
    updateScroll();
    window.addEventListener('scroll', updateScroll, { passive: true });
    window.addEventListener('resize', updateScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateScroll);
      window.removeEventListener('resize', updateScroll);
      clearTimeout(hideTimerRef.current);
    };
  }, [updateScroll]);

  if (!scrollInfo.hasOverflow) return null;

  return (
    <div
      ref={trackRef}
      className="custom-vertical-scrollbar"
      onClick={handleTrackClick}
      onPointerEnter={handleTrackEnter}
      onPointerLeave={handleTrackLeave}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: interacting ? '10px' : '6px',
        backgroundColor: interacting
          ? 'var(--border-subtle)'
          : 'var(--border-default)',
        opacity: visible || interacting ? 1 : 0,
        pointerEvents: visible || interacting ? 'auto' : 'none',
        transition: 'width 200ms cubic-bezier(0.23, 1, 0.32, 1), background-color 200ms ease, opacity 300ms ease',
        cursor: 'pointer',
        zIndex: 50,
      }}
    >
      <div
        onMouseDown={handleThumbDown}
        onTouchStart={handleThumbDown}
        style={{
          position: 'absolute',
          left: 0,
          top: `${scrollInfo.progress * (100 - scrollInfo.thumbPercent)}%`,
          width: '100%',
          height: `${scrollInfo.thumbPercent}%`,
          borderRadius: '99px',
          backgroundColor: interacting
            ? 'var(--color-primary-500)'
            : 'var(--color-primary-400)',
          cursor: draggingRef.current ? 'grabbing' : 'grab',
          touchAction: 'none',
          transition: 'background-color 150ms ease',
        }}
      />
    </div>
  );
};
